import type { ObligationResolution } from '@statura/utils';
import { decideVisibility } from './obligation-visibility';

export type ComplianceSurfaceVisibility = {
  qi?: 'VISIBLE' | 'REVIEW' | 'NOT_APPLICABLE';
  prudential?: 'VISIBLE' | 'REVIEW' | 'NOT_APPLICABLE';
};

export function complianceSurfaceForHref(
  href: string,
): keyof Pick<ComplianceSurfaceVisibility, 'qi' | 'prudential'> | null {
  const path = href.split(/[?#]/, 1)[0] ?? '';
  if (path === '/compliance/quality-indicators') return 'qi';
  if (path === '/compliance/prudential' || path.startsWith('/compliance/prudential/')) {
    return 'prudential';
  }
  return null;
}

export function isFactDrivenComplianceSurfaceVisible(
  href: string,
  visibility?: ComplianceSurfaceVisibility | null,
): boolean {
  const surface = complianceSurfaceForHref(href);
  return !surface || visibility?.[surface] !== 'NOT_APPLICABLE';
}

/**
 * Compliance hub tile href → catalogue obligation id, for tiles that map
 * cleanly to one T1 obligation (packages/utils/src/compliance-obligations/catalogue.ts).
 * A tile not listed here has no clean mapping and keeps today's
 * entitlement + ProgramTag logic untouched (per the orchestrator ruling).
 */
export const HUB_TILE_OBLIGATION_IDS: Readonly<Record<string, string>> = {
  '/compliance/restrictive-practices': 'restrictive_practices',
  '/compliance/rn-coverage': 'rn_24_7',
  '/compliance/prudential': 'financial_prudential_standards',
  '/compliance/whistleblower': 'whistleblower',
  '/compliance/standards': 'quality_standards_1_4',
  '/compliance/quality-indicators?tab=quarter': 'quality_indicator_program',
  '/compliance/incidents': 'sirs',
  '/compliance/complaints': 'complaints_feedback',
  '/compliance/data-breach': 'privacy_notifiable_data_breaches',
  '/compliance/ipc': 'ipc_lead',
  '/compliance/whs': 'whs',
};

/**
 * Resolver-first, legacy-fallback visibility for one hub tile.
 *
 * Entitlement is the outer boundary and is NOT decided here — callers must
 * gate on module entitlement (isModuleNavigationVisible) before calling
 * this, so the resolver can only ever narrow within an already-entitled
 * tile, never grant one the tenant is not entitled to.
 */
export function decideHubTileVisible(
  href: string,
  legacyVisible: boolean,
  resolutionsById: ReadonlyMap<string, ObligationResolution>,
  surfaceVisibility?: ComplianceSurfaceVisibility | null,
): boolean {
  if (!isFactDrivenComplianceSurfaceVisible(href, surfaceVisibility)) return false;
  const surface = complianceSurfaceForHref(href);
  // Fact-driven surfaces remain reachable while awaiting setup. The resolver's
  // legacy RAC result is a presentation hint and must not veto an authorised
  // provider-entity surface.
  if (surface) return surfaceVisibility?.[surface] !== 'NOT_APPLICABLE';
  const obligationId = HUB_TILE_OBLIGATION_IDS[href];
  if (!obligationId) return legacyVisible;
  return decideVisibility(resolutionsById.get(obligationId), legacyVisible).visible;
}
